/* eslint-disable class-methods-use-this */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import LayoutCard from 'components/FrictionlessCheckout/LayoutCard/LayoutCard';
import AccessPoint from 'components/FrictionlessCheckout/DeliverToReleaseThree/AccessPoint';
import AddressDisplay from 'components/FrictionlessCheckout/DeliverToReleaseThree/AddressDisplay';
import ShipToAccessPoint from 'components/FrictionlessCheckout/DeliverToReleaseThree/ShipToAccessPoint';
import SDDInstructions from 'components/FrictionlessCheckout/DeliverToReleaseThree/SDDInstructions';
import FrictionlessCheckoutConstants from 'utils/FrictionlessCheckoutConstants';
import ShipAddressSection from 'components/FrictionlessCheckout/DeliverToReleaseThree/ShipAddress/Section';
import { Box } from 'components/ui';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import checkoutUtils from 'utils/Checkout';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import anaConsts from 'analytics/constants';
import { SECTION_NAMES, FC_CHECKOUT_SECTIONS, FC_CHECKOUT_PATH } from 'constants/frictionlessCheckout';
import ShipToAccessPointModal from 'components/FrictionlessCheckout/DeliverToReleaseThree/ShipToAccessPoint/ShipToAccessPointModal/ShipToAccessPointModal';
import PickupPersonForm from 'components/FrictionlessCheckout/DeliverToReleaseThree/ShipToAccessPoint/PickupPersonForm';
import orderUtils from 'utils/Order';

const {
    ORDER_TYPES: { GET_IT_SHIPPED }
} = FrictionlessCheckoutConstants;
const { isHalAddress } = orderUtils;

const HAL_ADDRESS = 'HAL';

const ShippingAddressInformation = ({
    isHal,
    address,
    halOperatingHours,
    isHalAvailable,
    children,
    setIsEdit,
    holdAtLocation,
    showAccessPoint = true,
    showDisplayAddress = true,
    isEdit,
    shippingGroupId
}) => (
    <Box paddingX={[4, 4, isEdit ? 4 : 5]}>
        {showDisplayAddress && (
            <AddressDisplay
                address={address}
                isHal={isHal}
                halOperatingHours={halOperatingHours}
                holdAtLocation={holdAtLocation}
                isEdit={isEdit}
                shippingGroupId={shippingGroupId}
                callback={setIsEdit}
            >
                {isHalAvailable && showAccessPoint && (
                    <AccessPoint
                        isHal={isHal}
                        accessPointCallback={setIsEdit}
                        isEdit={false}
                    />
                )}
            </AddressDisplay>
        )}
        {children}
    </Box>
);

class DeliverTo extends BaseClass {
    state = {
        isHal: false,
        isEditByChangeLink: false,
        isSddWithoutAddressMatch: false,
        loadingAddress: this.props.isSdd,
        isNewAddressAdded: false,
        showPickupPersonModal: false,
        addressId: null,
        isAddingNewAddress: false,
        halAddressDraft: null
    };

    getSubtitle = () => {
        const { localization, isSdd, isAutoReplenishBasket, isGis } = this.props;
        const { sDDAndGISOrders, gISAndAROrders, sDDAndAROrders, sDDAndGISAndAROrders } = localization;
        let subtitle = null;

        if (isSdd && isGis && isAutoReplenishBasket) {
            subtitle = sDDAndGISAndAROrders;
        } else if (isGis && isAutoReplenishBasket) {
            subtitle = gISAndAROrders;
        } else if (isSdd && isAutoReplenishBasket) {
            subtitle = sDDAndAROrders;
        } else if (isSdd && isGis) {
            subtitle = sDDAndGISOrders;
        }

        return subtitle;
    };

    componentDidMount() {
        this.props.getAddressBook(false).then(data => {
            this.setState({ loadingAddress: false });
            this.props.updateAddressListWithHalAddress(true);
            FrictionlessUtils.updateCheckoutURLBasedOnOrderCompleteness(data?.addressList);
        });
        const isHal = this.props.shippingGroup.address?.addressType === HAL_ADDRESS;
        this.setState({ isHal }, () => {
            if (isHal) {
                this.props.updateHalAddress(this.props.shippingGroup.address);
            }
        });
    }

    componentDidUpdate(prevProps) {
        const isGuestCheckout = checkoutUtils.isGuestOrder();
        const { shippingGroup: prevShippingGroup, addressByPreferredZipCode: prevAddressByPreferredZipCode } = prevProps;
        const {
            shippingGroup, addressByPreferredZipCode, isSdd, giftCardShippingGroup, orderHasPhysicalGiftCard, isEditViewFromURL
        } = this.props;

        if (prevShippingGroup.address?.addressType !== shippingGroup.address?.addressType) {
            const isHal = shippingGroup.address?.addressType === HAL_ADDRESS;
            this.setState({ isHal, addressId: !isHal ? shippingGroup.address?.addressId : null });
        }

        if (
            (prevAddressByPreferredZipCode !== addressByPreferredZipCode && addressByPreferredZipCode > 1) ||
            (isGuestCheckout &&
                !this.props.isEditViewFromURL &&
                orderHasPhysicalGiftCard &&
                giftCardShippingGroup.isComplete &&
                !shippingGroup.address.email &&
                !giftCardShippingGroup.address.email &&
                !this.state.isEditByChangeLink)
        ) {
            this.setEditChangeLink(true, false);
        }

        if (
            isSdd &&
            !this.state.isSddWithoutAddressMatch &&
            !this.state.loadingAddress &&
            addressByPreferredZipCode === 0 &&
            !this.state.isNewAddressAdded
        ) {
            this.setState({ isSddWithoutAddressMatch: true });
        }

        if (!isEditViewFromURL && isEditViewFromURL !== prevProps.isEditViewFromURL && this.state.isEditByChangeLink) {
            this.setState({
                isEditByChangeLink: false
            });
        }
    }

    setPickupLocationEdit = (showPickupPersonModal, isEdit = false, halAddressDraft = null) => {
        const setDefault = this.props.addressList?.length === 0 ? false : isEdit;

        this.setState({
            showPickupPersonModal: showPickupPersonModal,
            isEditByChangeLink: setDefault,
            isAddingNewAddress: false,
            halAddressDraft
        });
    };

    setEditChangeLink = (isEditMode, isTriggeredByUserClick = false) => {
        this.setState({ isEditByChangeLink: isEditMode });

        if (isEditMode) {
            this.fireAnalytics(isTriggeredByUserClick);
            this.props.setActiveSection(this.props.sectionName);
            this.props.goToURL({ path: `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.DELIVER_TO.path}` });
        }

        if (!isEditMode && this.state.isHal) {
            this.props.updateAddressListWithHalAddress(true);
        }
    };

    fireAnalytics = isTriggeredByUserClick => {
        const isGuestCheckout = checkoutUtils.isGuestOrder();
        !isGuestCheckout &&
            FrictionlessCheckoutBindings.setChangeLinkAnalytics(
                anaConsts.PAGE_TYPES.SHIPPING,
                isTriggeredByUserClick,
                this.props.addressList?.length > 1
            );
    };

    setHalWhenChangeLink = (isEditMode, _, halAddressDraft = null) => {
        this.setState({ showPickupPersonModal: true, halAddressDraft });
    };

    setIsSddWithoutAddressMatch = isSddWithoutAddressMatch => {
        this.setState({ isSddWithoutAddressMatch, isNewAddressAdded: true });
    };

    setSelectedAddressId = addressId => {
        this.setState({ addressId });
    };

    setIsAddingNewAddress = isAddingNewAddress => {
        this.setState({ isAddingNewAddress });
    };

    renderModal = () => {
        const { localization, halOperatingHours, shippingGroup, isEditViewFromURL } = this.props;

        const { address, shippingGroupId } = shippingGroup;

        const { showPickupPersonModal } = this.state;

        const isGuestCheckout = checkoutUtils.isGuestOrder();
        const addressProp = this.state.halAddressDraft ?? address;

        if (addressProp && !addressProp?.firstName) {
            addressProp.firstName = address?.firstName;
            addressProp.lastName = address?.lastName;
        }

        return (
            <>
                {showPickupPersonModal && (
                    <ShipToAccessPointModal
                        onDismiss={this.setPickupLocationEdit}
                        isOpen={showPickupPersonModal}
                        deliverToEditMode={isEditViewFromURL}
                    >
                        <AddressDisplay
                            address={addressProp}
                            isHal
                            halOperatingHours={addressProp?.halOperatingHours ?? halOperatingHours}
                            holdAtLocation={localization.holdAtLocation}
                            callback={this.setPickupLocationEdit}
                            isDraft
                        />
                        <PickupPersonForm
                            shippingGroupId={shippingGroupId}
                            shippingAddress={addressProp}
                            isGuestCheckout={isGuestCheckout}
                            localization={localization}
                            callback={this.setPickupLocationEdit}
                            isEditMode={isEditViewFromURL}
                        />
                    </ShipToAccessPointModal>
                )}
            </>
        );
    };

    renderAddressBookChangeLink = () => {
        const {
            isZeroCheckout,
            addressList,
            isHalAvailable,
            isNewUserFlow,
            shippingGroup,
            orderHasPhysicalGiftCard,
            isSdd,
            isCollapsed,
            isEditViewFromURL
        } = this.props;

        const { address, isComplete, shippingGroupId, shippingMethod } = shippingGroup;

        const { isHal, isEditByChangeLink } = this.state;
        const isGuestCheckout = checkoutUtils.isGuestOrder();
        const hasJustHalAddress = isHalAddress(addressList[0]) && addressList.length === 1;
        const isRadio = addressList.length && !hasJustHalAddress && !isGuestCheckout && !this.state.isAddingNewAddress;

        return (
            <>
                {isEditByChangeLink && (addressList.length > 0 || (address && !address.isDraft)) && (
                    <Box
                        paddingX={[4, 4, 5]}
                        minHeight={133}
                    >
                        <ShipAddressSection
                            isNewUserFlow={isNewUserFlow && !addressList.length}
                            shippingAddress={address}
                            profileAddresses={addressList || []}
                            shippingGroupId={shippingGroupId}
                            isComplete={isComplete}
                            orderHasPhysicalGiftCard={orderHasPhysicalGiftCard}
                            isZeroCheckout={isZeroCheckout}
                            isGuestCheckout={isGuestCheckout}
                            shippingMethod={shippingMethod}
                            setAccessPoint={this.setEditChangeLink}
                            isHalAvailable={isHalAvailable}
                            isHal={isHal}
                            isSdd={isSdd}
                            isCollapsed={isCollapsed}
                            onAddressSelect={this.setSelectedAddressId}
                            setIsAddingNewAddress={this.setIsAddingNewAddress}
                            showTaxExemptAddress
                            accessPointCallback={!isHal ? this.setHalWhenChangeLink : this.setPickupLocationEdit}
                            showAddressFormForHalGuestCheckout={isHal && (isGuestCheckout || hasJustHalAddress) && isEditByChangeLink}
                            currentAddressListLength={addressList?.length || 0}
                            sectionName={SECTION_NAMES.DELIVER_TO}
                        >
                            {isHalAvailable && (
                                <AccessPoint
                                    isHal={isHal}
                                    accessPointCallback={!isHal ? this.setHalWhenChangeLink : this.setPickupLocationEdit}
                                    full
                                    addressId={this.state.addressId}
                                    accessPointAddress={address}
                                    isRadio={isRadio}
                                    isEdit={isEditViewFromURL}
                                />
                            )}
                        </ShipAddressSection>
                    </Box>
                )}
            </>
        );
    };

    // eslint-disable-next-line complexity
    renderCardBody = () => {
        const {
            localization,
            isZeroCheckout,
            addressList,
            isHalAvailable,
            isNewUserFlow,
            halOperatingHours,
            shippingGroup,
            orderHasPhysicalGiftCard,
            isSdd,
            isCollapsed,
            isEditViewFromURL
        } = this.props;

        const { address, isComplete, shippingGroupId, shippingMethod } = shippingGroup;

        const { isHal, isEditByChangeLink, isSddWithoutAddressMatch } = this.state;
        const isGuestCheckout = checkoutUtils.isGuestOrder();
        const isRadio = !isNewUserFlow && !isGuestCheckout && !this.state.isAddingNewAddress;

        return (
            <>
                {this.renderModal()}
                {/** Show address book - click change link  */}
                {this.renderAddressBookChangeLink()}
                {/** Shipping Address Information save state - not conflic with Sdd */}
                {!isSddWithoutAddressMatch && address && isComplete ? (
                    <ShippingAddressInformation
                        isHal={isHal}
                        address={address}
                        halOperatingHours={halOperatingHours}
                        isHalAvailable={isHalAvailable}
                        setIsEdit={this.setPickupLocationEdit}
                        showAccessPoint={!isEditViewFromURL && !isHal}
                        holdAtLocation={localization.holdAtLocation}
                        showDisplayAddress={!isEditByChangeLink}
                        isEdit={isEditViewFromURL || isEditByChangeLink}
                        shippingGroupId={shippingGroupId}
                    >
                        {isHal && !isEditViewFromURL && (
                            <ShipToAccessPoint
                                isComplete={isComplete}
                                shippingAddress={address}
                                editMode={isEditViewFromURL}
                                shippingGroupId={shippingGroupId}
                                callback={this.setPickupLocationEdit}
                                isGuestCheckout={isGuestCheckout}
                            />
                        )}
                    </ShippingAddressInformation>
                ) : (
                    <Box paddingX={[4, 4, 5]}>
                        <ShipAddressSection
                            isNewUserFlow={isNewUserFlow && !addressList.length}
                            shippingAddress={isSddWithoutAddressMatch && isEditViewFromURL ? null : address}
                            profileAddresses={isSddWithoutAddressMatch && isEditViewFromURL ? [] : addressList || []}
                            shippingGroupId={shippingGroupId}
                            isComplete={isComplete}
                            orderHasPhysicalGiftCard={orderHasPhysicalGiftCard}
                            isZeroCheckout={isZeroCheckout}
                            isGuestCheckout={isGuestCheckout}
                            shippingMethod={shippingMethod}
                            setAccessPoint={isSddWithoutAddressMatch ? this.setIsSddWithoutAddressMatch : this.setEditChangeLink}
                            isHalAvailable={isHalAvailable}
                            isSdd={isSdd}
                            isCollapsed={isCollapsed}
                            analyticsCallback={() => {
                                isSdd && !isEditByChangeLink && this.fireAnalytics();
                            }}
                            onAddressSelect={this.setSelectedAddressId}
                            setIsAddingNewAddress={this.setIsAddingNewAddress}
                            accessPointCallback={!isHal ? this.setHalWhenChangeLink : this.setPickupLocationEdit}
                            currentAddressListLength={addressList?.length || 0}
                            sectionName={SECTION_NAMES.DELIVER_TO}
                        >
                            {isHalAvailable && (
                                <AccessPoint
                                    isHal={isHal}
                                    accessPointCallback={!isHal ? this.setHalWhenChangeLink : this.setPickupLocationEdit}
                                    full
                                    addressId={this.state.addressId}
                                    isRadio={isRadio}
                                    setIsAddingNewAddress={this.setIsAddingNewAddress}
                                    isEdit={isEditViewFromURL}
                                />
                            )}
                        </ShipAddressSection>
                    </Box>
                )}
            </>
        );
    };

    render() {
        const {
            orderType,
            localization,
            sectionNumber,
            isSdd,
            isNewUserFlow,
            isCollapsed,
            hasSDUOnlyInSddBasket,
            sectionLevelError,
            hasCmsContent,
            isEditViewFromURL
        } = this.props;
        const { loadingAddress, isEditByChangeLink } = this.state;

        let title;

        if (orderType === GET_IT_SHIPPED) {
            title = localization.getItShippedTitle;
        }

        const subTitle = isCollapsed ? null : this.getSubtitle();

        return (
            <LayoutCard
                sectionInfo={{
                    sectionNumber: !sectionLevelError && sectionNumber,
                    title,
                    subTitle,
                    hasDivider: true,
                    isChangePermitted: !isEditViewFromURL,
                    onChangeClick: this.setEditChangeLink,
                    sectionLevelError,
                    sectionName: SECTION_NAMES.DELIVER_TO
                }}
                {...(hasCmsContent && { marginTop: [4, 4, 5] })}
                hasPaddingForChildren={false}
                isEditMode={(isEditViewFromURL || isNewUserFlow) && !isCollapsed}
                buttonCustomStyle={styles}
                isNewUserFlow={isNewUserFlow}
                isCollapsed={isCollapsed}
                ariaLabel={localization.deliveryAndPickupInformation}
                role='region'
            >
                {loadingAddress && isSdd ? (
                    <Box
                        paddingX={[4, 4, 5]}
                        minHeight={133}
                    />
                ) : (
                    <>
                        {this.renderCardBody()}
                        {isSdd && !hasSDUOnlyInSddBasket && !isEditViewFromURL && !isEditByChangeLink && <SDDInstructions />}
                    </>
                )}
            </LayoutCard>
        );
    }
}

const styles = {
    button: {
        width: '48%'
    }
};

export default wrapComponent(DeliverTo, 'DeliverTo', true);
